import React, { useState } from 'react';
import { Lock, Unlock, ArrowRight, UserPlus, Heart, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import MemberCard from './MemberCard';
import UnlockSequence from './UnlockSequence';
import StatsDashboard from './StatsDashboard';
import { PrivacyPolicy, TermsOfService, ContactUs, CustomerAgreement } from './LegalPages';
import cardTexture from '../assets/card_texture.jpg';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:4000';

const Members = ({ user, setUser, onOpenArchive }) => {
    const [mode, setMode] = useState('login'); // 'login', 'signup', 'selection'
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [status, setStatus] = useState('locked'); // 'locked', 'success', 'error', 'loading'
    const [message, setMessage] = useState('');
    const [showPrivacy, setShowPrivacy] = useState(false);
    const [showTerms, setShowTerms] = useState(false);
    const [showContact, setShowContact] = useState(false);
    const [showAgreement, setShowAgreement] = useState(false);
    const [isUnlocking, setIsUnlocking] = useState(false);
    const [isEjecting, setIsEjecting] = useState(false);
    const [showArchive, setShowArchive] = useState(false);
    const [favorites, setFavorites] = useState([]);
    const [selectedFavorite, setSelectedFavorite] = useState(null);
    const [scale, setScale] = useState(1);
    const containerRef = React.useRef(null);

    React.useEffect(() => {
        const updateScale = () => {
            // Only apply scale logic if NOT in authenticated stats dashboard mode
            if (containerRef.current && (!user || !user.isSubscribed || isUnlocking || isEjecting)) {
                const { clientWidth, clientHeight } = containerRef.current;
                // Base dimensions for the chassis (including some padding buffer)
                const baseWidth = 420;
                const baseHeight = 620;

                // Calculate scale to fit within container, maxing out at reasonable size
                const scaleX = clientWidth / baseWidth;
                const scaleY = clientHeight / baseHeight;
                const newScale = Math.min(scaleX, scaleY) * 0.9; // 0.9 for 90% fill

                setScale(newScale);
            } else {
                setScale(1); // Reset scale for responsive mode
            }
        };

        // Initial calc
        updateScale();

        const observer = new ResizeObserver(updateScale);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => observer.disconnect();
    }, [user, isUnlocking, isEjecting]); // Re-run scale logic when mode changes

    React.useEffect(() => {
        console.log("Current API_BASE:", API_BASE);
        // ... existing logic ...
    }, []);

    const handleAuth = async (e) => {
        e.preventDefault();
        setStatus('loading');
        setMessage('');

        let endpoint = '';
        let body = {};

        if (mode === 'login') {
            endpoint = `${API_BASE}/api/auth/login`;
            body = { email, password };
        } else if (mode === 'signup') {
            endpoint = `${API_BASE}/api/auth/signup`;
            body = { email, password };
        } else if (mode === 'forgot') {
            endpoint = `${API_BASE}/api/auth/forgot-password`;
            body = { email };
        } else if (mode === 'reset') {
            endpoint = `${API_BASE}/api/auth/reset-password`;
            const urlParams = new URLSearchParams(window.location.search);
            const token = urlParams.get('token');
            body = { token, newPassword: password };
        }

        try {
            const res = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });

            const data = await res.json();

            if (res.ok) {
                setStatus('success');
                if (mode === 'login') {
                    if (!data.user.isSubscribed) {
                        setUser(data.user);
                        localStorage.setItem('paradise_user', JSON.stringify(data.user));
                        localStorage.setItem('paradise_token', data.token);
                        setMode('selection');
                        setMessage('Please select a membership plan.');
                    } else {
                        setIsUnlocking(true);
                        setUser(data.user);
                        localStorage.setItem('paradise_user', JSON.stringify(data.user));
                        localStorage.setItem('paradise_token', data.token);
                        setMessage('Welcome back.');
                    }
                } else if (mode === 'signup') {
                    // Signup success -> Move to Selection
                    setUser(data.user); // Store user locally but don't show member card yet
                    // localStorage.setItem('paradise_user', JSON.stringify(data.user)); // Optional: persist
                    setMode('selection');
                    setMessage('Select your membership tier.');
                } else if (mode === 'forgot') {
                    setMessage('Reset link sent (check server console).');
                } else if (mode === 'reset') {
                    setMessage('Password updated. Please login.');
                    setTimeout(() => setMode('login'), 2000);
                }
            } else {
                setStatus('error');
                setMessage(data.message || 'Authentication failed');
                setTimeout(() => setStatus('locked'), 3000);
            }
        } catch (err) {
            setStatus('error');
            setMessage('Connection error');
            setTimeout(() => setStatus('locked'), 3000);
        }
    };

    const [billingInterval, setBillingInterval] = useState('monthly'); // 'monthly' 'yearly'

    const handleSubscribe = async (tier, interval) => {
        if (!user) return;

        try {
            const referralCode = localStorage.getItem('paradise_referral');
            const res = await fetch(`${API_BASE}/api/stripe/checkout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: user.id,
                    email: user.email,
                    referralCode,
                    plan: interval, // 'monthly' or 'yearly'
                    tier: tier // 'access', 'club', 'executive'
                })
            });

            const data = await res.json();
            if (data.url) {
                window.location.href = data.url;
            }
        } catch (err) {
            console.error('Stripe error:', err);
        }
    };

    const handlePortal = async () => {
        if (!user) return;
        setStatus('loading');

        try {
            const res = await fetch(`${API_BASE}/api/stripe/portal`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: user.id })
            });

            const data = await res.json();

            if (res.ok && data.url) {
                window.location.href = data.url;
            } else {
                setStatus('error');
                setMessage(data.message || 'Failed to load portal');
                setTimeout(() => setStatus('success'), 3000);
            }
        } catch (err) {
            console.error('Portal error:', err);
            setStatus('error');
            setMessage('Connection error');
            setTimeout(() => setStatus('success'), 3000);
        }
    };

    const handleManageSubscription = () => {
        if (user && user.isSubscribed) {
            handlePortal();
        } else {
            // If logged in but not subscribed, go to selection
            setMode('selection');
        }
    };

    const logout = () => {
        setUser(null);
        localStorage.removeItem('paradise_user');
        setMode('login');
        setIsUnlocking(false);
        setIsEjecting(false);
    };

    const handleEjectClick = () => {
        setIsEjecting(true);
    };

    const handleEjectComplete = () => {
        logout();
    };

    const handleArchiveClick = async () => {
        if (onOpenArchive) {
            onOpenArchive();
        } else {
            // Mobile / Inline Fallback
            setShowArchive(true);
            try {
                const token = localStorage.getItem('paradise_token');
                if (!token) return;
                const res = await fetch(`${API_BASE}/api/favorites/${user.id}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setFavorites(data.favorites || []);
                }
            } catch (error) {
                console.error("Error fetching favorites:", error);
            }
        }
    };

    const handleCloseArchive = () => {
        setShowArchive(false);
        setSelectedFavorite(null);
    };

    const TIERS = [
        {
            id: 'access',
            name: 'ACCESS Member',
            priceMonthly: 5.00,
            priceYearly: 30.00,
            color: '#a0a0a0',
            textColor: '#333',
            features: ['Latest Set Preview (5 Images)', 'SD Quality Photo', 'Limited Video Previews'],
            gradient: 'linear-gradient(135deg, #e0e0e0, #bdbdbd)'
        },
        {
            id: 'club',
            name: 'CLUB Member',
            priceMonthly: 18.50,
            priceYearly: 111.00,
            color: '#594e36',
            textColor: '#594e36',
            features: ['Review 2 Months Archive', 'HD Quality Photo', 'Full Video Streaming', 'Comment Access'],
            gradient: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 20%), linear-gradient(to bottom, #cfc09f, #a69279, #cfc09f)',
            isPopular: true
        },
        {
            id: 'executive',
            name: 'EXECUTIVE Member',
            priceMonthly: 173.00,
            priceYearly: 1038.00,
            color: '#d4af37',
            textColor: '#8a7030',
            features: ['Unlimited Vault Archive', '4K Source Quality', 'Full Downloads', 'Direct Creator DMs', 'Early Access'],
            gradient: 'linear-gradient(135deg, #1a1a1a, #000000)'
        }
    ];

    // Determine currently active view mode
    const isMemberView = user && user.isSubscribed && !isUnlocking && !isEjecting;

    return (
        <div className={`h-full w-full bg-[#dcdad3] font-mono select-none overflow-hidden`} ref={containerRef}>
            {/* Scrollable Container for Authenticated Members */}
            <div className={`w-full h-full flex flex-col ${isMemberView ? 'overflow-y-auto items-center' : 'items-center justify-center'}`}>

                {/* 
                  Layout Logic:
                  If NOT member view: Use the scaled fixed-size chassis centered in view.
                  If IS member view: Use a responsive max-width container with normal flow.
                */}
                <div
                    className={`${isMemberView
                        ? 'w-full max-w-4xl px-4 py-8 mt-4 transition-all duration-500 ease-out'
                        : 'bg-[#e8e6df] rounded-lg shadow-[inset_0_2px_5px_rgba(255,255,255,0.8),0_10px_20px_rgba(0,0,0,0.1)] border border-white/50 p-6 relative origin-center transition-transform duration-75 ease-out'
                        }`}
                    style={!isMemberView ? {
                        width: '380px', // Fixed base width for login
                        transform: `scale(${scale})`
                    } : {}}
                >
                    {/* Fixed Chassis Decorations (Only in non-member view) */}
                    {!isMemberView && (
                        <>
                            <div className="absolute top-3 left-3 w-2 h-2 rounded-full bg-[#bdbaa8] shadow-[inset_1px_1px_2px_rgba(0,0,0,0.2)] flex items-center justify-center"><div className="w-1.5 h-0.5 bg-[#a8a595] rotate-45"></div></div>
                            <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-[#bdbaa8] shadow-[inset_1px_1px_2px_rgba(0,0,0,0.2)] flex items-center justify-center"><div className="w-1.5 h-0.5 bg-[#a8a595] rotate-45"></div></div>
                            <div className="absolute bottom-3 left-3 w-2 h-2 rounded-full bg-[#bdbaa8] shadow-[inset_1px_1px_2px_rgba(0,0,0,0.2)] flex items-center justify-center"><div className="w-1.5 h-0.5 bg-[#a8a595] rotate-45"></div></div>
                            <div className="absolute bottom-3 right-3 w-2 h-2 rounded-full bg-[#bdbaa8] shadow-[inset_1px_1px_2px_rgba(0,0,0,0.2)] flex items-center justify-center"><div className="w-1.5 h-0.5 bg-[#a8a595] rotate-45"></div></div>
                        </>
                    )}

                    {/* Header / Status - Refactor for Wide View */}
                    <div className={`flex items-center justify-between mb-6 ${isMemberView ? 'max-w-2xl mx-auto w-full px-2' : 'mb-2'}`}>
                        <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full shadow-[0_0_5px_rgba(0,0,0,0.2)] transition-colors duration-300 ${user && user.isSubscribed ? 'bg-green-500 shadow-green-500/50' : status === 'error' ? 'bg-red-500 shadow-red-500/50' : status === 'loading' ? 'bg-yellow-500 animate-pulse' : 'bg-red-900'}`}></div>
                            <span className="text-[10px] font-bold uppercase tracking-widest text-[#666]">
                                {user && user.isSubscribed ? 'MEMBER ACCESS' : mode === 'selection' ? 'INITIATING MEMBERSHIP' : 'RESTRICTED ACCESS'}
                            </span>
                        </div>
                        <div className="text-[#999]">
                            {user && user.isSubscribed ? <Unlock size={18} /> : <Lock size={18} />}
                        </div>
                    </div>

                    {/* Content Area */}
                    {mode === 'selection' ? (
                        <div className="flex flex-col h-full">
                            <div className="flex items-center justify-between mb-4 px-1">
                                <div className="text-[10px] text-[#888] uppercase tracking-widest">
                                    Select Tier
                                </div>
                                {/* Billing Toggle */}
                                <div className="flex items-center space-x-2 bg-[#d0d0d0] p-1 rounded-full shadow-inner">
                                    <button
                                        onClick={() => setBillingInterval('monthly')}
                                        className={`px-3 py-1 rounded-full text-[8px] font-bold uppercase tracking-widest transition-all ${billingInterval === 'monthly' ? 'bg-[#f0f0f0] text-[#333] shadow-sm' : 'text-[#666] hover:text-[#444]'}`}
                                    >
                                        Monthly
                                    </button>
                                    <button
                                        onClick={() => setBillingInterval('yearly')}
                                        className={`px-3 py-1 rounded-full text-[8px] font-bold uppercase tracking-widest transition-all ${billingInterval === 'yearly' ? 'bg-[#333] text-[#fff] shadow-sm' : 'text-[#666] hover:text-[#444]'}`}
                                    >
                                        Yearly <span className="text-[6px] text-green-400 ml-1">(-50%)</span>
                                    </button>
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto pr-1 -mr-2 space-y-3 pb-2 custom-scrollbar">
                                {TIERS.map((tier) => (
                                    <button
                                        key={tier.id}
                                        onClick={() => handleSubscribe(tier.id, billingInterval)}
                                        className="w-full text-left group relative"
                                    >
                                        <div
                                            className={`relative w-full rounded-xl overflow-hidden shadow-lg group-hover:scale-[1.01] transition-transform duration-300 border border-black/5 ${tier.id === 'executive' ? 'h-[140px]' : 'h-[110px]'}`}
                                            style={{
                                                background: tier.gradient,
                                            }}
                                        >
                                            {/* Texture Overlay */}
                                            <div className="absolute inset-0 bg-cover bg-center opacity-30 mix-blend-overlay"
                                                style={{ backgroundImage: `url(${cardTexture})` }}
                                            />

                                            {/* Noise Texture */}
                                            <div className="absolute inset-0 opacity-15 pointer-events-none mix-blend-multiply"
                                                style={{
                                                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
                                                }}
                                            />

                                            {/* Content */}
                                            <div className="relative z-10 p-4 h-full flex flex-col justify-between">
                                                <div className="flex justify-between items-start">
                                                    <div className="space-y-1">
                                                        <div className="font-mono text-lg font-bold tracking-widest uppercase"
                                                            style={{
                                                                fontFamily: '"OCR A Std", "Courier New", monospace',
                                                                color: tier.id === 'executive' ? '#d4af37' : tier.textColor,
                                                                textShadow: tier.id === 'executive' ? '0 1px 2px rgba(0,0,0,0.8)' : '1px 1px 0px rgba(255,255,255,0.4)'
                                                            }}
                                                        >
                                                            {tier.name}
                                                        </div>
                                                        <div className="text-xs uppercase font-bold tracking-wider opacity-90" style={{ color: tier.id === 'executive' ? '#8a7030' : tier.textColor }}>
                                                            ${billingInterval === 'monthly' ? tier.priceMonthly.toFixed(2) : tier.priceYearly.toFixed(2)} / {billingInterval === 'monthly' ? 'mo' : 'yr'}
                                                        </div>
                                                    </div>
                                                    {tier.isPopular && (
                                                        <div className="px-2 py-1 bg-[#AF2E1B] text-white text-[7px] font-bold uppercase tracking-widest rounded shadow-sm">
                                                            Best Value
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="space-y-1 mt-2">
                                                    {tier.features.slice(0, 3).map((feat, i) => (
                                                        <div key={i} className="text-[8px] uppercase tracking-wider font-mono opacity-80 flex items-center gap-1" style={{ color: tier.id === 'executive' ? '#aaa' : '#555' }}>
                                                            <div className="w-1 h-1 bg-current rounded-full"></div> {feat}
                                                        </div>
                                                    ))}
                                                    {tier.features.length > 3 && (
                                                        <div className="text-[7px] uppercase tracking-widest opacity-60 italic" style={{ color: tier.id === 'executive' ? '#888' : '#777' }}>
                                                            + {tier.features.length - 3} more...
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>

                            <button
                                type="button"
                                onClick={() => logout()}
                                className="w-full py-2 text-[9px] text-[#888] hover:text-[#555] uppercase tracking-widest underline decoration-dotted mt-2 flex-none"
                            >
                                Cancel
                            </button>
                        </div>
                    ) : !user ? (
                        <>
                            <div className="text-center text-[10px] text-[#888] uppercase tracking-widest mb-4">
                                Restricted access for approved members.
                            </div>
                            <form onSubmit={handleAuth} className="space-y-4">
                                <div className="bg-[#222] p-4 rounded shadow-[inset_0_2px_8px_rgba(0,0,0,0.5)] border-b border-white/10 relative overflow-hidden">
                                    {/* Screen Glare */}
                                    <div className="absolute top-0 right-0 w-full h-1/2 bg-gradient-to-b from-white/5 to-transparent pointer-events-none"></div>

                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-[8px] font-bold uppercase tracking-widest text-[#666] mb-1">Identity</label>
                                            <input
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                className="bg-transparent border-none outline-none w-full font-mono text-sm text-[#4ade80] placeholder-[#4ade80]/20 uppercase"
                                                placeholder="EMAIL ADDRESS"
                                                required
                                                autoComplete="off"
                                                name="email_field_no_autofill"
                                            />
                                        </div>
                                        <div className="border-t border-white/10 pt-2">
                                            <label className="block text-[8px] font-bold uppercase tracking-widest text-[#666] mb-1">Passcode</label>
                                            <input
                                                type="password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                className="bg-transparent border-none outline-none w-full font-mono text-sm text-[#4ade80] placeholder-[#4ade80]/20 uppercase"
                                                placeholder="••••••••"
                                                required
                                                autoComplete="new-password"
                                                name="password_field_no_autofill"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {message && <div className="text-center text-[10px] text-red-500 font-bold uppercase tracking-widest">{message}</div>}

                                <div className="flex space-x-2">
                                    <button
                                        type="submit"
                                        className="flex-1 h-10 rounded bg-[#e0e0e0] hover:bg-white text-[#333] active:bg-[#dcdad3] flex items-center justify-center space-x-2 transition-all shadow-[0_2px_0_rgba(0,0,0,0.1),0_3px_5px_rgba(0,0,0,0.1)] active:shadow-none active:translate-y-[2px]"
                                    >
                                        <span className="text-[10px] font-bold uppercase tracking-widest">
                                            {mode === 'login' ? 'Login' : mode === 'signup' ? 'Sign Up' : mode === 'forgot' ? 'Send Link' : 'Reset Password'}
                                        </span>
                                        <ArrowRight size={12} />
                                    </button>
                                </div>

                                <div className="text-center pt-2 space-y-3">
                                    {mode === 'login' && (
                                        <button
                                            type="button"
                                            onClick={() => setMode('forgot')}
                                            className="block w-full text-[9px] text-[#888] hover:text-[#555] uppercase tracking-widest underline decoration-dotted mb-2"
                                        >
                                            Forgot Password?
                                        </button>
                                    )}

                                    {mode === 'login' ? (
                                        <div className="flex flex-col gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setMode('signup')}
                                                className="w-full h-12 rounded bg-[#333] text-white shadow-lg hover:bg-black hover:scale-[1.02] flex items-center justify-center space-x-2 transition-all uppercase tracking-widest font-bold text-[10px]"
                                            >
                                                <UserPlus size={14} />
                                                <span>Join as Member</span>
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => window.location.href = '/apply'}
                                                className="w-full h-12 rounded border-2 border-[#333] text-[#333] hover:bg-[#333] hover:text-white flex items-center justify-center space-x-2 transition-all uppercase tracking-widest font-bold text-[10px]"
                                            >
                                                <span>Become a Creator</span>
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => setMode('login')}
                                            className="w-full h-10 rounded text-[#888] hover:text-[#555] underline decoration-dotted uppercase tracking-widest font-bold text-[10px]"
                                        >
                                            Back to Login
                                        </button>
                                    )}
                                </div>
                            </form>
                        </>
                    ) : (
                        <div className={`flex flex-col items-center w-full h-full ${isMemberView ? '' : 'min-h-[300px]'}`}>

                            {/* Card / Animation Container */}
                            <div className={`flex items-center justify-center w-full flex-1 relative ${isMemberView ? 'py-8' : 'py-4 px-4 min-h-[240px]'}`}>
                                {isUnlocking ? (
                                    <UnlockSequence onUnlockComplete={() => setIsUnlocking(false)} mode="unlock" tier={user.membershipTier || 'gold'} />
                                ) : isEjecting ? (
                                    <UnlockSequence onUnlockComplete={handleEjectComplete} mode="eject" tier={user.membershipTier || 'gold'} />
                                ) : showArchive ? (
                                    <div className="w-full h-[505px] bg-[#e6e4dc] rounded-lg p-1 flex flex-col relative overflow-hidden shadow-[inset_0_2px_6px_rgba(0,0,0,0.1)] border border-[#c0c0c0] z-10">
                                        {/* ... existing archive UI ... */}
                                        <div className="flex items-center justify-between px-3 py-2 bg-[#dcdad3] border-b border-[#bbb] shadow-sm relative z-10">
                                            <div className="flex items-center gap-2">
                                                <div className="p-1 bg-[#fff] rounded-full shadow-sm border border-[#ccc]">
                                                    <Heart size={10} className="text-[#AF2E1B] fill-current" />
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-[9px] font-bold uppercase tracking-widest text-[#444]">Personal Archive</span>
                                                    <span className="text-[7px] font-mono text-[#777] uppercase tracking-wide">SECURE STORAGE</span>
                                                </div>
                                            </div>
                                            <button
                                                onClick={handleCloseArchive}
                                                className="w-6 h-6 flex items-center justify-center bg-[#d0d0d0] hover:bg-[#c0c0c0] rounded-full shadow-[0_1px_2px_rgba(0,0,0,0.1)] border border-[#bbb] transition-all active:scale-95"
                                            >
                                                <X size={12} className="text-[#555]" />
                                            </button>
                                        </div>

                                        <div className="flex-1 overflow-y-auto p-3 bg-[#e0e0e0] relative shadow-[inset_0_4px_8px_rgba(0,0,0,0.05)] custom-scrollbar">
                                            <div className="absolute inset-0 opacity-10 pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/cardboard.png')]"></div>

                                            {selectedFavorite ? (
                                                <div className="flex flex-col h-full relative z-10 overflow-hidden">
                                                    <div className="flex-none mb-3 pl-1">
                                                        <button onClick={() => setSelectedFavorite(null)} className="text-[9px] font-bold uppercase tracking-widest text-[#666] flex items-center gap-1 hover:text-[#333]">
                                                            <ArrowRight className="rotate-180" size={10} /> Back to Grid
                                                        </button>
                                                    </div>
                                                    <div className="flex-1 min-h-0 flex items-center justify-center bg-[#1a1a1a] rounded-lg border border-[#333] relative overflow-hidden shadow-md p-1">
                                                        <img src={selectedFavorite.imageUrl} alt={selectedFavorite.title} className="max-w-full max-h-full object-contain" />
                                                    </div>
                                                    <div className="flex-none mt-3 text-center pb-2">
                                                        <div className="text-[10px] font-bold uppercase text-[#333] tracking-wide">{selectedFavorite.title}</div>
                                                        <div className="text-[8px] font-mono text-[#666] uppercase tracking-wider mt-0.5">{selectedFavorite.photographer}</div>
                                                    </div>
                                                </div>
                                            ) : favorites.length > 0 ? (
                                                <div className="grid grid-cols-2 gap-3 relative z-10">
                                                    {favorites.map((fav, idx) => (
                                                        <button
                                                            key={idx}
                                                            onClick={() => setSelectedFavorite(fav)}
                                                            className="aspect-square bg-[#fff] p-1 rounded-md shadow-[0_2px_4px_rgba(0,0,0,0.05)] border border-[#ccc] hover:border-[#AF2E1B] transition-all relative group active:scale-95"
                                                        >
                                                            <div className="w-full h-full bg-[#111] overflow-hidden rounded-sm relative">
                                                                <img src={fav.imageUrl} alt={fav.title} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="h-full flex flex-col items-center justify-center text-[#888] space-y-3 opacity-60 relative z-10">
                                                    <Heart size={32} strokeWidth={1.5} />
                                                    <span className="text-[9px] font-mono uppercase tracking-widest">Archive Empty</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    // MEMBER CARD
                                    <MemberCard
                                        userName={user.name || (user.email ? user.email.split('@')[0] : 'MEMBER')}
                                        memberSinceDate={user.memberSince || "11/23"}
                                        onLogout={null}
                                        tier={user.membershipTier || 'gold'}
                                        onManageSubscription={handleManageSubscription}
                                    />
                                )}
                            </div>

                            {/* Action Buttons (Archive / Eject) */}
                            {user && !isUnlocking && !isEjecting && !showArchive && (
                                <div className="mb-8 flex gap-4 relative z-10">
                                    <button
                                        onClick={handleArchiveClick}
                                        className="group relative px-4 py-2 bg-[#d0d0d0] rounded shadow-[0_4px_0_#999,0_5px_10px_rgba(0,0,0,0.2)] active:shadow-none active:translate-y-[4px] border border-[#bbb] transition-all"
                                    >
                                        <div className="flex items-center gap-2 text-[#444] font-bold text-xs uppercase tracking-widest group-hover:text-[#AF2E1B] transition-colors">
                                            <Heart size={14} className="group-hover:fill-current" />
                                            <span>ARCHIVE</span>
                                        </div>
                                    </button>

                                    <button
                                        onClick={handleEjectClick}
                                        className="group relative px-6 py-2 bg-[#d0d0d0] rounded shadow-[0_4px_0_#999,0_5px_10px_rgba(0,0,0,0.2)] active:shadow-none active:translate-y-[4px] border border-[#bbb] transition-all"
                                    >
                                        <div className="flex items-center gap-2 text-[#444] font-bold text-xs uppercase tracking-widest group-hover:text-[#AF2E1B] transition-colors">
                                            <span>EJECT CARD</span>
                                            <div className="w-2 h-2 bg-[#444] group-hover:bg-[#AF2E1B] rounded-full animate-pulse"></div>
                                        </div>
                                    </button>
                                </div>
                            )}

                            {/* Unsubscribed Call to Action */}
                            {user && !user.isSubscribed && !isUnlocking && mode !== 'selection' && (
                                <div className="mb-8 mt-2">
                                    <button
                                        onClick={() => setMode('selection')}
                                        className="group relative px-8 py-3 bg-[#AF2E1B] rounded shadow-[0_4px_0_#7a1f12,0_5px_10px_rgba(0,0,0,0.2)] active:shadow-none active:translate-y-[4px] border border-[#d63a23] transition-all animate-pulse"
                                    >
                                        <div className="flex items-center gap-2 text-white font-bold text-xs uppercase tracking-widest">
                                            <span>COMPLETE REGISTRATION</span>
                                            <ArrowRight size={14} />
                                        </div>
                                    </button>
                                </div>
                            )}

                            {/* STATS DASHBOARD (NEW) */}
                            {isMemberView && (
                                <div className="w-full mt-12 bg-[#a3a19b]/10 rounded-xl p-0.5 border-t border-white/20">
                                    <StatsDashboard
                                        stats={user.stats}
                                        tier={user.membershipTier || 'access'}
                                    />
                                </div>
                            )}
                        </div>
                    )
                    }

                    {/* Footer */}
                    <div className={`${isMemberView ? 'mt-12 mb-8' : 'mt-6'} text-center space-y-2`}>
                        <div className="flex items-center justify-center space-x-4 text-[8px] text-[#888] uppercase tracking-widest">
                            <button onClick={() => setShowPrivacy(true)} className="hover:text-[#555] hover:underline">Privacy Policy</button>
                            <span>•</span>
                            <button onClick={() => setShowTerms(true)} className="hover:text-[#555] hover:underline">Terms of Service</button>
                            <span>•</span>
                            <button onClick={() => setShowContact(true)} className="hover:text-[#555] hover:underline">Contact Us</button>
                            <span>•</span>
                            <button onClick={() => setShowAgreement(true)} className="hover:text-[#555] hover:underline">Customer Agreement</button>
                        </div>
                        <div className="text-[8px] text-[#999] uppercase tracking-widest">
                            Secure Terminal v2.0
                        </div>
                    </div>
                </div >

                {/* Legal Modals */}
                <AnimatePresence>
                    {showPrivacy && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                            <div className="w-full max-w-2xl h-[80vh] rounded shadow-2xl overflow-hidden relative">
                                <PrivacyPolicy onClose={() => setShowPrivacy(false)} />
                            </div>
                        </motion.div>
                    )}
                    {showTerms && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                            <div className="w-full max-w-2xl h-[80vh] rounded shadow-2xl overflow-hidden relative">
                                <TermsOfService onClose={() => setShowTerms(false)} />
                            </div>
                        </motion.div>
                    )}
                    {showContact && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                            <div className="w-full max-w-lg h-[50vh] rounded shadow-2xl overflow-hidden relative">
                                <ContactUs onClose={() => setShowContact(false)} />
                            </div>
                        </motion.div>
                    )}
                    {showAgreement && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                            <div className="w-full max-w-2xl h-[80vh] rounded shadow-2xl overflow-hidden relative">
                                <CustomerAgreement onClose={() => setShowAgreement(false)} />
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div >
        </div >
    );
};

export default Members;
